import React from 'react';
import {useMatch, useParams} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';

import {ECurrency} from '@edna/utils';

import {EChannelType} from 'src/constants';
import {useGetLeadClientQuery} from 'src/containers/LeadCompanyClients/api';
import {useGetSupportClientQuery} from 'src/containers/SupportClients/api';
import {EResellerIds, useGetTenantDataQuery} from 'src/containers/User';
import {useEdnaLinks} from 'src/hooks';
import PATHS from 'src/paths';

const WHATSAPP_PRICING_LINK_RU = 'https://edna.ru/whatsapp_conv_pricing/';
const WHATSAPP_PRICING_LINK_EUR = 'https://edna.io/pricing-whatsapp-cbp-eur/';
const WHATSAPP_PRICING_LINK_USD = 'https://edna.io/pricing-whatsapp-cbp-usd/';
const WHATSAPP_PRICING_LINK_KZ = 'https://edna.kz/kz/whatsapp_conv_pricing/';

const VIBER_PRICING_LINK_RU = 'https://edna.ru/viberbm_pricing/';
const VIBER_PRICING_LINK_EUR = 'https://edna.io/pricing-viberbm-eur/';
const VIBER_PRICING_LINK_USD = 'https://edna.io/pricing-viberbm-usd/';
const VIBER_PRICING_LINK_KZ = 'https://edna.kz/page/ViberBM_pricing';

export const usePricingLink = ({
  channelType,
  usePublicPrices,
  subjectId,
}: {
  channelType: string;
  subjectId?: number;
  usePublicPrices?: boolean;
}) => {
  const {id} = useParams<'id'>();

  const isPersonalPrices = !!useMatch({path: PATHS.COMPANY_PROFILE, end: false}) || usePublicPrices;
  const isSupportView = !!useMatch({path: PATHS.SUPPORT_CLIENT_CARD, end: false});
  const isSkip = !!subjectId;

  const {data: tenantData} = useGetTenantDataQuery(undefined, {skip: isSkip || !isPersonalPrices});

  const {data: leadClient} = useGetLeadClientQuery(id ?? skipToken, {
    skip: isSkip || isPersonalPrices || isSupportView,
  });
  const {data: supportClient} = useGetSupportClientQuery(id ?? skipToken, {
    skip: isSkip || isPersonalPrices || !isSupportView,
  });

  const resellerId = tenantData?.resellerId ?? leadClient?.resellerId ?? supportClient?.resellerId;
  const currency =
    tenantData?.defaultCurrency ?? leadClient?.currencyCode ?? supportClient?.defaultCurrency;

  const links = useEdnaLinks(resellerId);

  const pricingLink = React.useMemo(() => {
    if (!resellerId) {
      return null;
    }

    if (channelType === EChannelType.SMS) {
      return links.SMS_PRICING;
    }

    if (channelType === EChannelType.VIBER) {
      switch (resellerId) {
        case EResellerIds.MFM_ID:
        case EResellerIds.PT_ID:
          if (currency === ECurrency.EUR) {
            return VIBER_PRICING_LINK_EUR;
          }

          return VIBER_PRICING_LINK_USD;
        case EResellerIds.KZ_ID:
          return VIBER_PRICING_LINK_KZ;
        default:
          return VIBER_PRICING_LINK_RU;
      }
    }

    if (channelType === EChannelType.WHATSAPP) {
      switch (resellerId) {
        case EResellerIds.MFM_ID:
        case EResellerIds.PT_ID:
          if (currency === ECurrency.EUR) {
            return WHATSAPP_PRICING_LINK_EUR;
          }

          return WHATSAPP_PRICING_LINK_USD;
        case EResellerIds.KZ_ID:
          return WHATSAPP_PRICING_LINK_KZ;
        default:
          return WHATSAPP_PRICING_LINK_RU;
      }
    }
  }, [resellerId, channelType, links, currency]);

  return pricingLink || '';
};
